// Studby — Photo Solver. Upload a problem photo → GPT-4o step-by-step solution.
import { useState, useRef } from "react";
import { Camera, Upload, Loader2, RotateCcw } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Markdown } from "@/components/Markdown";
import { Paywall, TrialBanner } from "@/components/Paywall";

export default function PhotoSolver() {
  const { hasAccess } = useAuth();
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState("");
  const [solution, setSolution] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const inputRef = useRef(null);

  if (!hasAccess) return <Paywall />;

  const pickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setPreview(URL.createObjectURL(f));
    setSolution("");
    setError("");
  };

  const solve = async () => {
    if (!file) return;
    setLoading(true);
    setError("");
    setSolution("");
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/solve", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setSolution(data.solution);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setFile(null);
    setPreview("");
    setSolution("");
    setError("");
  };

  return (
    <div>
      <TrialBanner />
      <h1 className="font-heading text-3xl font-extrabold tracking-tight mb-1">Photo Solver</h1>
      <p className="text-muted-foreground mb-6">
        Snap or upload a problem — Studby explains every step.
      </p>

      {/* Upload area */}
      {!preview ? (
        <button
          onClick={() => inputRef.current?.click()}
          data-testid="upload-area"
          className="w-full border-2 border-dashed border-border rounded-2xl bg-card py-14 flex flex-col items-center gap-3 hover:border-accent transition-colors"
        >
          <span className="grid place-items-center w-14 h-14 rounded-2xl bg-secondary text-primary">
            <Camera size={26} aria-hidden="true" />
          </span>
          <span className="font-medium">Tap to take or upload a photo</span>
          <span className="text-xs text-muted-foreground">Any subject — homework, textbooks, worksheets</span>
        </button>
      ) : (
        <div className="bg-card border border-border rounded-2xl p-4">
          <img src={preview} alt="Problem to solve" className="rounded-xl w-full object-contain max-h-[45vh]" />
          <div className="flex gap-2 mt-4">
            <Button onClick={solve} disabled={loading} className="rounded-full flex-1" data-testid="solve-button">
              {loading ? (<><Loader2 size={18} className="mr-1.5 animate-spin" aria-hidden="true" /> Solving…</>) : "Solve it"}
            </Button>
            <Button onClick={reset} variant="outline" className="rounded-full" data-testid="reset-button">
              <RotateCcw size={16} aria-hidden="true" />
            </Button>
          </div>
        </div>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={pickFile}
        className="hidden"
        data-testid="photo-input"
      />

      {error && <p className="text-sm text-destructive mt-4" data-testid="solve-error">{error}</p>}

      {solution && (
        <div className="bg-card border border-border rounded-2xl p-5 sm:p-6 mt-6">
          <Markdown testId="solution-content">{solution}</Markdown>
        </div>
      )}

      {!solution && !preview && (
        <div className="mt-6">
          <Button variant="outline" onClick={() => inputRef.current?.click()} className="rounded-full" data-testid="upload-button-alt">
            <Upload size={16} className="mr-1.5" aria-hidden="true" /> Choose from gallery
          </Button>
        </div>
      )}
    </div>
  );
}
